import { calculatedFieldPath } from 'resources/rate_settings/utils/fieldsVisibility';

export type RateCode = 'professionalRate' | 'baseMargin' | 'baseChargeRate' | 'chargeRate';
export type RateValues = Record<RateCode, number>;
export type RateVersion =
  | 'professionalRateAndBaseMargin'
  | 'professionalRateAndBaseChargeRate'
  | 'professionalRateAndChargeRate'
  | 'baseMarginAndBaseChargeRate'
  | 'baseMarginAndChargeRate';

export const CONTROLLABLE_CODES: RateCode[] = [
  'professionalRate',
  'baseMargin',
  'baseChargeRate',
  'chargeRate',
];
export const DEFAULT_CONTROLLED: RateCode[] = ['baseMargin', 'chargeRate'];
export const CONTROLLED_RATE_FIELDS_KEY = 'controlled_rate_fields';
// Client-only flag: set once the admin actually interacts with a tab's rate fields
// (typing or toggling). The transform uses it to drop controller-computed snapshots
// of inherited values for tabs the admin never touched; stripped from the payload.
export const RATE_CONTROLLER_ENGAGED_KEY = 'rate_controller_engaged';

// Union of every field recompute() may write across the three tabs. When a tab
// disengages (the admin reverts all of their input) these are reset to their form
// defaults so controller snapshots of inherited values are not submitted as own.
export const CONTROLLER_WRITTEN_FIELDS = [
  'pro_rate_cents',
  'base_charge_rate_cents',
  'charge_rate_cents',
  'base_margin_percent',
  'base_margin_percent_w2',
  'base_margin_percent_w2_travel',
  'margin_percent_1099',
  'total_margin_percent_w2',
  'margin_percent_w2_travel',
  'margin_percent_w2_tcv',
  'blended_rate_cents',
  'invoicing_markup',
  'misc_markup',
  'operational_markup',
];

const pairKey = (firstCode: RateCode, secondCode: RateCode) =>
  [firstCode, secondCode].sort().join('|');

const EXCLUDED_PAIR = pairKey('baseChargeRate', 'chargeRate');

const VERSION_MAP: Record<string, RateVersion> = {
  [pairKey('professionalRate', 'baseMargin')]: 'professionalRateAndBaseMargin',
  [pairKey('professionalRate', 'baseChargeRate')]: 'professionalRateAndBaseChargeRate',
  [pairKey('professionalRate', 'chargeRate')]: 'professionalRateAndChargeRate',
  [pairKey('baseMargin', 'baseChargeRate')]: 'baseMarginAndBaseChargeRate',
  [pairKey('baseMargin', 'chargeRate')]: 'baseMarginAndChargeRate',
};

export const versionOf = (controlled: RateCode[]): RateVersion | null => {
  if (controlled.length !== 2) {
    return null;
  }
  return VERSION_MAP[pairKey(controlled[0], controlled[1])] ?? null;
};

export type ChargeRateMarkups = {
  invoicingMarkup?: number;
  miscMarkup?: number;
  operationalMarkup?: number;
};

export const markupFactorOf = ({
  invoicingMarkup = 0,
  miscMarkup = 0,
  operationalMarkup = 0,
}: ChargeRateMarkups): number =>
  (1 + (Number(invoicingMarkup) || 0)) *
  (1 + (Number(miscMarkup) || 0)) *
  (1 + (Number(operationalMarkup) || 0));

export const solveRates = ({
  controlled,
  values,
  markups,
  blendedFactor = 1,
  burdenDivisor = 1,
}: {
  controlled: RateCode[];
  values: RateValues;
  markups: ChargeRateMarkups;
  blendedFactor?: number;
  // W2 Local folds employer burdens into the effective margin, so the professional
  // rate is discounted by (1 + burdens). Defaults to 1 (no burdens) for ten99/travel.
  burdenDivisor?: number;
}): RateValues => {
  const compoundingMarkups = markupFactorOf(markups);
  const modifier = Number(blendedFactor) > 0 ? Number(blendedFactor) : 1;
  const burdens = Number(burdenDivisor) > 0 ? Number(burdenDivisor) : 1;
  let { professionalRate, baseMargin, baseChargeRate, chargeRate } = values;

  switch (versionOf(controlled)) {
    case 'professionalRateAndBaseMargin':
      baseChargeRate = (professionalRate * burdens) / ((1 - baseMargin) * modifier);
      chargeRate = baseChargeRate * compoundingMarkups;
      break;
    case 'professionalRateAndBaseChargeRate':
      baseMargin = 1 - (professionalRate * burdens) / (baseChargeRate * modifier);
      chargeRate = baseChargeRate * compoundingMarkups;
      break;
    case 'professionalRateAndChargeRate':
      baseChargeRate = chargeRate / compoundingMarkups;
      baseMargin = 1 - (professionalRate * burdens) / (baseChargeRate * modifier);
      break;
    case 'baseMarginAndBaseChargeRate':
      professionalRate = (baseChargeRate * modifier * (1 - baseMargin)) / burdens;
      chargeRate = baseChargeRate * compoundingMarkups;
      break;
    case 'baseMarginAndChargeRate':
      baseChargeRate = chargeRate / compoundingMarkups;
      professionalRate = (baseChargeRate * modifier * (1 - baseMargin)) / burdens;
      break;
    default:
      break;
  }

  return { professionalRate, baseMargin, baseChargeRate, chargeRate };
};

// Keep exactly 2 controlled, never baseChargeRate+chargeRate together: drop the
// oldest unless that would leave the forbidden pair.
export const swapControlled = (controlled: RateCode[], clicked: RateCode): RateCode[] => {
  if (controlled.includes(clicked)) {
    return controlled;
  }
  const [oldest, newest] = controlled;
  // Drop the oldest and add clicked...
  if (pairKey(newest, clicked) !== EXCLUDED_PAIR) {
    return [newest, clicked];
  }
  // ...unless that would create the forbidden pair — then drop the newest.
  return [oldest, clicked];
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// ''/null/undefined mean "apply inheritance" — no override.
export const toBlendedOverride = (value: unknown): boolean | undefined =>
  value === true || value === 'true'
    ? true
    : value === false || value === 'false'
    ? false
    : undefined;

export const deriveInheritedRatePreview = ({
  inherited,
  source,
  isLocationLevel,
  useBlendedOverride,
}: {
  inherited: any;
  source: string;
  isLocationLevel: boolean;
  // The form's live structure selection, when the admin made one.
  useBlendedOverride?: boolean;
}): {
  professionalRate: number | null;
  baseChargeRate: number | null;
  blendedRateCents: number | null;
  chargeRate: number | null;
} => {
  const isW2Local = source.includes('w2.local');

  const chargeRate = toNumber(inherited?.charge_rate_cents);
  const modifiedChargeRate = toNumber(inherited?.modified_charge_rate_cents);
  const fetchedBlendedCharge = toNumber(inherited?.charge_blended_rate_cents);
  const blendedMarkup = toNumber(inherited?.blended_rates?.blended_rate_markup);

  const multiplier = markupFactorOf({
    invoicingMarkup: toNumber(inherited?.invoicing_markup) ?? 0,
    miscMarkup: toNumber(inherited?.misc_markup) ?? 0,
    operationalMarkup: source.includes('w2') ? toNumber(inherited?.operational_markup) ?? 0 : 0,
  });

  const inheritedBlended = Boolean(inherited?.use_blended_rate);
  const effectiveBlended = useBlendedOverride ?? inheritedBlended;
  // Location-level anchors bake the active structure in; market/state are 8hr-anchored.
  const emittedBlended = isLocationLevel && inheritedBlended;
  const rawFactor = 1 + (blendedMarkup ?? 0);
  const factor = rawFactor > 0 ? rawFactor : 1;
  const anchorScale =
    effectiveBlended === emittedBlended ? 1 : effectiveBlended ? factor : 1 / factor;
  const scaled = (rate: number | null): number | null =>
    rate === null ? null : rate * anchorScale;

  const chargeDerivedBase =
    chargeRate !== null && multiplier > 0
      ? chargeRate / multiplier / (emittedBlended ? factor : 1)
      : null;
  const fetchedBase = toNumber(inherited?.base_charge_rate_cents);
  // At location level the resolved charge carries the location_type_markup but the
  // fetched base does not — and saving an own base zeroes that markup — so the
  // inherited base previews charge-derived, with the markup folded in.
  const baseChargeRate = isLocationLevel
    ? chargeDerivedBase ?? fetchedBase
    : fetchedBase ?? chargeDerivedBase;

  const baseMargin = toNumber(inherited?.[baseMarginField(source).split('.').pop() as string]);
  const finalMargin = toNumber(
    source.includes('w2.travel')
      ? inherited?.margin_percent_w2_travel
      : isW2Local
      ? inherited?.total_margin_percent_w2
      : inherited?.margin_percent_1099,
  );

  // Only market/state fetches carry a genuine 12hr charge_blended; at location
  // level it is active-structure-anchored, so cross-structure previews rescale.
  const effectiveChargeRate =
    effectiveBlended && !emittedBlended && !isLocationLevel
      ? fetchedBlendedCharge ?? scaled(chargeRate)
      : scaled(chargeRate);
  const chargeRateForBaseMargin =
    modifiedChargeRate !== null
      ? modifiedChargeRate * anchorScale
      : baseChargeRate === null
      ? null
      : baseChargeRate * (effectiveBlended ? factor : 1);

  let professionalRate: number | null = null;
  if (finalMargin !== null && effectiveChargeRate !== null) {
    professionalRate = effectiveChargeRate * (1 - finalMargin);
  } else if (!isW2Local && baseMargin !== null && chargeRateForBaseMargin !== null) {
    professionalRate = chargeRateForBaseMargin * (1 - baseMargin);
  }

  const blendedRateCents =
    fetchedBlendedCharge ??
    toNumber(inherited?.blended_rate_cents) ??
    (chargeRate !== null && blendedMarkup !== null ? chargeRate * (1 + blendedMarkup) : null);

  return { professionalRate, baseChargeRate, blendedRateCents, chargeRate: effectiveChargeRate };
};

export const baseMarginField = (source: string): string => {
  if (source.includes('w2.local')) {
    return `${source}.base_margin_percent_w2`;
  }
  if (source.includes('w2.travel')) {
    return `${source}.base_margin_percent_w2_travel`;
  }
  return `${source}.base_margin_percent`;
};

export const codeToField = (
  code: RateCode,
  source: string,
  getValue?: (path: string) => unknown,
): string => {
  switch (code) {
    case 'professionalRate':
      return `${source}.pro_rate_cents`;
    case 'baseChargeRate':
      return `${source}.base_charge_rate_cents`;
    case 'chargeRate':
      return calculatedFieldPath(source, 'charge_rate_cents', getValue);
    case 'baseMargin':
      return baseMarginField(source);
  }
};

// Editing the 12hr Charge Rate (blended_rate_cents) is modeled as an update to
// blended_rate_markup (the source of truth). Returns null for an unusable charge
// base or a negative implied markup (caller should not write in those cases).
export const deriveBlendedMarkup = (blendedCents: number, chargeCents: number): number | null => {
  if (!Number.isFinite(chargeCents) || chargeCents <= 0) {
    return null;
  }
  const markup = Math.round((blendedCents / chargeCents - 1) * 10000) / 10000;
  return markup >= 0 ? markup : null;
};
